import type {
  DataGridColumnSettings,
  DataGridDataRowTemplateProps,
  DataGridSettings,
  JsonData,
  RowTemplateType,
} from '@myosh/odin-components';
import { DataGrid, type DataGridRowIndicator } from '@myosh/odin-components';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useFetchLinkedRecordColumnValues } from '../../../../hooks/use-fetch-linked-record-column-values';
import useFetchRecords from '../../../../hooks/use-fetch-records';
import useGridSettings from '../../../../hooks/use-grid-settings';
import useRenderDataGrid from '../../../../hooks/use-render-data-grid';
import { DataGridLoader } from '../../../dynamic-page/data-grid-loader.component';

interface LinkedRecordsGridProps {
  recordIds: Array<string>;
  formId?: number;
  configId?: number;
  defaultGridSettings: DataGridSettings;
  columnConfigurator: (configColumn: DataGridColumnSettings) => DataGridColumnSettings;
  rowIndicator?: DataGridRowIndicator;
  onRemoved: (id: string) => void;
}

// Displays all records that are already linked to the field
export default function LinkedRecordsGrid({
  recordIds,
  formId,
  configId,
  defaultGridSettings,
  columnConfigurator,
  rowIndicator,
  onRemoved,
}: LinkedRecordsGridProps) {
  const [gridData, setGridData] = useState<JsonData>([]);
  const [linkedRecordsGridSettings, setLinkedRecordsGridSettings] = useState<DataGridSettings>();

  const {
    settings: gridSettings,
    error: gridSettingsError,
    loading: gridSettingsLoading,
  } = useGridSettings(defaultGridSettings, columnConfigurator, formId, configId);

  const { records } = useFetchRecords(recordIds, true);

  const { fetchLinkedRecordColumnValues, cancelLinkedRecordColumnValuesPendingRequests } =
    useFetchLinkedRecordColumnValues(formId ?? 0);

  useEffect(() => {
    return () => {
      cancelLinkedRecordColumnValuesPendingRequests();
    };
  }, [cancelLinkedRecordColumnValuesPendingRequests]);

  useEffect(() => {
    if (!records || !gridSettings || gridSettingsLoading) {
      return;
    }

    if (records.length === 0) {
      return;
    }

    const data = records as unknown as Record<string, unknown>[];

    // Fetch linked records and inject into row data before sending to grid
    fetchLinkedRecordColumnValues(gridSettings.columns, data).then((linkedRecordsMap) => {
      const dataWithLinkedRecords = data.map((row) => ({
        ...row,
        _linkedRecordColumnValuesMap: linkedRecordsMap,
      }));

      setGridData(dataWithLinkedRecords);
    });
  }, [records, gridSettings, gridSettingsLoading, fetchLinkedRecordColumnValues]);

  useEffect(() => {
    if (!gridSettingsLoading && gridSettings?.columns) {
      setLinkedRecordsGridSettings({
        ...gridSettings,
        initialFieldFilters: [],
        initialSortedFields: [],
      });
    }
  }, [gridSettings, gridSettingsLoading]);

  const removeLinkedRecord = (id: string) => onRemoved(id);

  const linkedRowTemplate: RowTemplateType = (
    props: DataGridDataRowTemplateProps,
    children: ReactNode | ReactNode[]
  ) => (
    <div className="flex" onClick={() => removeLinkedRecord(String(props.data['id']))}>
      {children}
    </div>
  );

  const canRenderDataGrid = !gridSettingsLoading && !!gridSettings?.columns && !!linkedRecordsGridSettings?.columns;
  const { renderDataGrid } = useRenderDataGrid(canRenderDataGrid);

  return (
    <div className="flex h-full flex-col gap-4">
      {!renderDataGrid && !gridSettingsError && <DataGridLoader />}
      {canRenderDataGrid && (
        <>
          <DataGrid
            data={gridData}
            gridSettings={linkedRecordsGridSettings}
            rowIndicator={rowIndicator}
            rowTemplate={linkedRowTemplate}
            showGridMenu={false}
          />
          <div className="px-10 font-semibold">
            {gridData.length}/{gridData.length}
          </div>
        </>
      )}
    </div>
  );
}
